<script setup>
import { computed } from 'vue';
import ButtonV4 from 'next/button/Button.vue';

const props = defineProps({
  title: { type: String, required: true },
  used: { type: Number, default: 0 },
  // A missing limit means the plan does not cap this resource.
  limit: { type: Number, default: null },
  actionLabel: { type: String, default: '' },
});

defineEmits(['action']);

const isUnlimited = computed(() => !props.limit);

const percent = computed(() => {
  if (isUnlimited.value) return 100;
  return Math.min(100, Math.round((props.used / props.limit) * 100));
});

const barClass = computed(() => {
  if (isUnlimited.value) return 'bg-n-slate-6';
  if (percent.value >= 100) return 'bg-n-ruby-10';
  if (percent.value > 80) return 'bg-n-amber-10';
  return 'bg-n-blue-10';
});
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div class="flex items-baseline justify-between gap-2">
      <span
        class="text-xs font-medium tracking-wider uppercase text-n-slate-10"
      >
        {{ title }}
      </span>
      <span v-if="!isUnlimited" class="text-xs tabular-nums text-n-slate-11">
        {{ $t('BILLING_SETTINGS.USAGE.PERCENT_USED', { percent }) }}
      </span>
    </div>
    <div class="text-lg font-medium tabular-nums text-n-slate-12">
      <template v-if="isUnlimited">
        {{ $t('BILLING_SETTINGS.USAGE.UNLIMITED', { count: used }) }}
      </template>
      <template v-else>
        {{ $t('BILLING_SETTINGS.USAGE.USED_OF_LIMIT', { used, limit }) }}
      </template>
    </div>
    <div class="w-full h-2 overflow-hidden rounded-full bg-n-slate-4">
      <div class="h-2" :class="barClass" :style="{ width: `${percent}%` }" />
    </div>
    <ButtonV4
      v-if="actionLabel"
      sm
      faded
      slate
      class="mt-auto w-fit"
      @click="$emit('action')"
    >
      {{ actionLabel }}
    </ButtonV4>
  </div>
</template>
